import React, { useState, useEffect, useMemo } from 'react';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { View, Text, Alert, KeyboardAvoidingView, Platform, ScrollView, StyleSheet, Pressable, FlatList } from 'react-native';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useNavigation } from '@react-navigation/native';
import apis from '@api/api';
import { YStack, XStack, SizableText, Separator, Input } from 'tamagui';
import Button from '@components/UI/Button/Button';
import InputFieldStyled from '@components/Global/InputFieldStyled';
import { Loading } from '@components/Global/Loading';
import * as Animatable from 'react-native-animatable';
import { LinearGradient } from 'expo-linear-gradient';
import { Feather } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';

// Helper function to safely merge styles for web compatibility
// This prevents indexed property errors on web by ensuring styles are merged into plain objects
const mergeStyles = (...styles: any[]): any => {
  // Filter out null/undefined/false styles
  const validStyles = styles.filter((style): style is object => {
    return style != null && style !== false && typeof style === 'object';
  });

  if (validStyles.length === 0) {
    return {};
  }

  if (Platform.OS === 'web') {
    // For web, ALWAYS merge all styles into a single plain object to avoid indexed property issues
    // This prevents React Native Web from trying to set indexed properties on CSSStyleDeclaration
    const merged: Record<string, any> = {};

    const processStyle = (style: any) => {
      if (!style || typeof style !== 'object') return;

      if (Array.isArray(style)) {
        // If it's an array, process each element
        style.forEach(processStyle);
      } else {
        // For objects (including StyleSheet objects), copy all enumerable properties
        // This handles both plain objects and StyleSheet-created objects
        for (const key in style) {
          if (Object.prototype.hasOwnProperty.call(style, key) && !/^\d+$/.test(key)) {
            // Only copy non-numeric keys to avoid indexed properties
            merged[key] = style[key];
          }
        }
      }
    };

    validStyles.forEach(processStyle);
    return merged;
  }

  // For native, return array if multiple styles, single object if one
  return validStyles.length === 1 ? validStyles[0] : validStyles;
};

interface JournalEntry {
  _id: string;
  title: string;
  description: string;
  category: string;
  createdAt: string;
  updatedAt: string;
  tags?: string[];
}

interface GroupedEntries {
  [key: string]: JournalEntry[];
}

const JournalEntryScreen = () => {
  const theme = themeSelector();
  const navigation = useNavigation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Journal');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // New state for entries list
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [showEntries, setShowEntries] = useState(true);
  const [selectedCompany] = useSelector((state: DefaultRootState) => [state.app.selectedProfile]);

  // Auto-fill title with current date on mount
  useEffect(() => {
    const today = new Date();
    const dateString = today.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    setTitle(`Journal Entry - ${dateString}`);
    fetchEntries();
  }, []);

  // Fetch journal entries
  const fetchEntries = async () => {
    setIsLoadingEntries(true);
    try {
      const linkedId = selectedCompany?._id || null;
      const response = await apis.getInternalKnowledgePaginated(linkedId, {
        page: 1,
        limit: 100, // Fetch more entries for grouping
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      if (response?.data?.records) {
        // Filter for journal entries (those with journal tags)
        const journalEntries = response.data.records.filter((entry: any) =>
          entry.tags?.some((tag: string) =>
            tag.toLowerCase().includes('journal') ||
            tag.toLowerCase().includes('daily-journal')
          ) ||
          entry.category?.toLowerCase() === 'journal'
        );
        setEntries(journalEntries);
      }
    } catch (error) {
      console.error('Error fetching journal entries:', error);
    } finally {
      setIsLoadingEntries(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!title.trim()) {
      Alert.alert('Validation Error', 'Please enter a title for your journal entry.');
      return;
    }

    if (!description.trim()) {
      Alert.alert('Validation Error', 'Please enter your journal entry.');
      return;
    }

    if (!category.trim()) {
      Alert.alert('Validation Error', 'Please enter a category.');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await apis.postInternalKnowledge({
        title: title.trim(),
        description: description.trim(),
        category: category.trim(),
        type: 'internal',
        tags: ['daily-journal', 'journal-entry']
      });

      if (result) {
        // Refresh entries list
        await fetchEntries();
        // Reset form
        const today = new Date();
        const dateString = today.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        setTitle(`Journal Entry - ${dateString}`);
        setDescription('');
        setCategory('Journal');

        Alert.alert(
          'Success',
          'Your journal entry has been saved to your knowledge base!',
          [{ text: 'OK' }]
        );
      }
    } catch (error) {
      console.error('Error saving journal entry:', error);

      // Alert.alert(
      //   'Error',
      //   'Failed to save your journal entry. Please try again.',
      //   [{ text: 'OK' }]
      // );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter and search entries
  const filteredEntries = useMemo(() => {
    let filtered = entries;

    // Filter by category
    if (filterCategory !== 'all') {
      filtered = filtered.filter(entry =>
        entry.category?.toLowerCase() === filterCategory.toLowerCase()
      );
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(entry =>
        entry.title?.toLowerCase().includes(query) ||
        entry.description?.toLowerCase().includes(query) ||
        entry.category?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [entries, searchQuery, filterCategory]);

  // Group entries based on list size
  const groupedEntries = useMemo(() => {
    const groups: GroupedEntries = {};
    const shouldGroup = filteredEntries.length > 10; // Group if more than 10 entries

    filteredEntries.forEach(entry => {
      const date = new Date(entry.createdAt);
      let groupKey: string;

      if (shouldGroup) {
        // Group by month and year for large lists
        groupKey = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      } else {
        // Group by date for smaller lists
        groupKey = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(entry);
    });

    return groups;
  }, [filteredEntries]);

  // Get unique categories for filter
  const categories = useMemo(() => {
    const cats = new Set(entries.map(e => e.category).filter(Boolean));
    return Array.from(cats).sort();
  }, [entries]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Truncate description
  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <DefaultScreen backButton >
      <View style={{ flex: 1 }}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={{ flex: 1 }}
        >
          <ScrollView
            contentContainerStyle={{
              flexGrow: 1,
              paddingBottom: 40
            }}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* Previous Entries List */}
            {showEntries && (
              <Animatable.View
                animation="fadeInDown"
                duration={400}
                style={mergeStyles(
                  heroStyles.glassCard,
                  { marginBottom: 20 },
                  Platform.OS === 'web' && webOnlyStyles?.glassCard
                )}
              >
                <View style={mergeStyles(
                  heroStyles.contentContainer,
                  Platform.OS === 'web' && webOnlyStyles?.contentContainer
                )}>
                  <YStack gap={16} padding={20}>
                    {/* Header with toggle */}
                    <XStack justifyContent="space-between" alignItems="center">
                      <SizableText
                        fontSize="$7"
                        fontWeight="bold"
                        color={theme ? '#1f2937' : '#f9fafb'}
                      >
                        Previous Entries ({filteredEntries.length})
                      </SizableText>
                      <Pressable onPress={() => setShowEntries(!showEntries)}>
                        <Feather
                          name={showEntries ? "chevron-up" : "chevron-down"}
                          size={24}
                          color={theme ? '#1f2937' : '#f9fafb'}
                        />
                      </Pressable>
                    </XStack>

                    {/* Search and Filters */}
                    <YStack gap={12}>
                      {/* Search Input */}
                      <View style={heroStyles.inputContainer}>
                        <XStack alignItems="center" gap={8} paddingHorizontal={12}>
                          <Feather name="search" size={18} color={theme ? '#6b7280' : '#9ca3af'} />
                          <Input
                            placeholder="Search entries..."
                            value={searchQuery}
                            onChangeText={setSearchQuery}
                            backgroundColor="transparent"
                            borderWidth={0}
                            flex={1}
                            color={theme ? '#1f2937' : '#f9fafb'}
                            placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                            fontSize={14}
                          />
                          {searchQuery.length > 0 && (
                            <Pressable onPress={() => setSearchQuery('')}>
                              <Feather name="x" size={18} color={theme ? '#6b7280' : '#9ca3af'} />
                            </Pressable>
                          )}
                        </XStack>
                      </View>

                      {/* Category Filter */}
                      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                        <XStack gap={8}>
                          <Pressable
                            onPress={() => setFilterCategory('all')}
                            style={mergeStyles(
                              heroStyles.filterChip,
                              filterCategory === 'all' && heroStyles.filterChipActive
                            )}
                          >
                            <Text style={mergeStyles(
                              heroStyles.filterChipText,
                              filterCategory === 'all' && heroStyles.filterChipTextActive
                            )}>
                              All
                            </Text>
                          </Pressable>
                          {categories.map(cat => (
                            <Pressable
                              key={cat}
                              onPress={() => setFilterCategory(cat)}
                              style={mergeStyles(
                                heroStyles.filterChip,
                                filterCategory === cat && heroStyles.filterChipActive
                              )}
                            >
                              <Text style={mergeStyles(
                                heroStyles.filterChipText,
                                filterCategory === cat && heroStyles.filterChipTextActive
                              )}>
                                {cat}
                              </Text>
                            </Pressable>
                          ))}
                        </XStack>
                      </ScrollView>
                    </YStack>

                    {/* Entries List */}
                    {isLoadingEntries ? (
                      <View style={{ padding: 40, alignItems: 'center' }}>
                        <Loading />
                      </View>
                    ) : Object.keys(groupedEntries).length === 0 ? (
                      <View style={{ padding: 40, alignItems: 'center' }}>
                        <Feather name="book-open" size={48} color={theme ? '#9ca3af' : '#6b7280'} />
                        <SizableText
                          fontSize="$4"
                          color={theme ? '#6b7280' : '#9ca3af'}
                          marginTop={12}
                          textAlign="center"
                        >
                          {searchQuery || filterCategory !== 'all'
                            ? 'No entries match your search'
                            : 'No journal entries yet'}
                        </SizableText>
                      </View>
                    ) : (
                      <YStack gap={20}>
                        {Object.entries(groupedEntries).map(([groupKey, groupEntries]) => (
                          <YStack key={groupKey} gap={12}>
                            {/* Group Header */}
                            <XStack alignItems="center" gap={8}>
                              <Separator flex={1} borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)'} />
                              <SizableText
                                fontSize="$5"
                                fontWeight="600"
                                color={theme ? '#6b7280' : '#9ca3af'}
                              >
                                {groupKey}
                              </SizableText>
                              <Separator flex={1} borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)'} />
                            </XStack>

                            {/* Group Entries */}
                            <YStack gap={8}>
                              {groupEntries.map(entry => (
                                <Pressable
                                  key={entry._id}
                                  style={mergeStyles(
                                    heroStyles.entryCard,
                                    {
                                      backgroundColor: theme
                                        ? 'rgba(200, 162, 200, 0.05)'
                                        : 'rgba(200, 162, 200, 0.1)'
                                    }
                                  )}
                                >
                                  <YStack gap={8} padding={12}>
                                    <XStack justifyContent="space-between" alignItems="flex-start">
                                      <YStack flex={1} gap={4}>
                                        <SizableText
                                          fontSize="$5"
                                          fontWeight="600"
                                          color={theme ? '#1f2937' : '#f9fafb'}
                                          numberOfLines={2}
                                        >
                                          {entry.title}
                                        </SizableText>
                                        <SizableText
                                          fontSize="$3"
                                          color={theme ? '#6b7280' : '#9ca3af'}
                                        >
                                          {formatDate(entry.createdAt)}
                                        </SizableText>
                                      </YStack>
                                      {entry.category && (
                                        <View style={heroStyles.categoryBadge}>
                                          <Text style={heroStyles.categoryBadgeText}>
                                            {entry.category}
                                          </Text>
                                        </View>
                                      )}
                                    </XStack>
                                    {entry.description && (
                                      <SizableText
                                        fontSize="$4"
                                        color={theme ? '#4b5563' : '#d1d5db'}
                                        numberOfLines={3}
                                      >
                                        {truncateText(entry.description, 150)}
                                      </SizableText>
                                    )}
                                  </YStack>
                                </Pressable>
                              ))}
                            </YStack>
                          </YStack>
                        ))}
                      </YStack>
                    )}
                  </YStack>
                </View>
              </Animatable.View>
            )}

            {/* Glass Card Container */}
            <Animatable.View
              animation="fadeInUp"
              duration={600}
              style={mergeStyles(
                heroStyles.glassCard,
                Platform.OS === 'web' && webOnlyStyles?.glassCard
              )}
            >
              {/* Content Container */}
              <View style={mergeStyles(
                heroStyles.contentContainer,
                Platform.OS === 'web' && webOnlyStyles?.contentContainer
              )}>
                <YStack flex={1} gap={20} padding={20}>
                  {/* Header */}
                  <Animatable.View animation="fadeIn" delay={100} duration={800}>
                    <YStack gap={8}>
                      <SizableText
                        fontSize="$8"
                        fontWeight="bold"
                        color={theme ? '#1f2937' : '#f9fafb'}
                      >
                        Daily Journal Entry
                      </SizableText>
                      <SizableText
                        fontSize="$4"
                        color={theme ? '#6b7280' : '#9ca3af'}
                      >
                        Document today's big events to help AI understand your business context
                      </SizableText>
                    </YStack>
                  </Animatable.View>

                  <Separator borderColor={theme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.15)'} />

                  {/* Title Input */}
                  <Animatable.View animation="fadeInUp" delay={200} duration={600}>
                    <YStack gap={8}>
                      <SizableText
                        fontSize="$5"
                        fontWeight="600"
                        color={theme ? '#374151' : '#e5e7eb'}
                      >
                        Title
                      </SizableText>
                      <View style={heroStyles.inputContainer}>
                        <InputFieldStyled
                          value={title}
                          onChangeText={setTitle}
                          placeholder="Journal Entry - Date"
                          placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                          style={mergeStyles(
                            heroStyles.inputField,
                            {
                              color: theme ? '#1f2937' : '#f9fafb',
                              backgroundColor: 'transparent'
                            }
                          )}
                        />
                      </View>
                    </YStack>
                  </Animatable.View>

                  {/* Description Input */}
                  <Animatable.View animation="fadeInUp" delay={300} duration={600} style={{ flex: 1 }}>
                    <YStack gap={8} flex={1}>
                      <SizableText
                        fontSize="$5"
                        fontWeight="600"
                        color={theme ? '#374151' : '#e5e7eb'}
                      >
                        Journal Entry
                      </SizableText>
                      <View style={mergeStyles(heroStyles.inputContainer, { flex: 1, minHeight: 200 })}>
                        <InputFieldStyled
                          value={description}
                          onChangeText={setDescription}
                          placeholder="Write about today's big events, meetings, decisions, or important moments..."
                          placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                          multiline
                          textAlignVertical="top"
                          style={mergeStyles(
                            heroStyles.inputField,
                            {
                              flex: 1,
                              color: theme ? '#1f2937' : '#f9fafb',
                              backgroundColor: 'transparent'
                            }
                          )}
                        />
                      </View>
                    </YStack>
                  </Animatable.View>

                  {/* Category Input */}
                  <Animatable.View animation="fadeInUp" delay={400} duration={600}>
                    <YStack gap={8}>
                      <SizableText
                        fontSize="$5"
                        fontWeight="600"
                        color={theme ? '#374151' : '#e5e7eb'}
                      >
                        Category
                      </SizableText>
                      <View style={heroStyles.inputContainer}>
                        <InputFieldStyled
                          value={category}
                          onChangeText={setCategory}
                          placeholder="Journal"
                          placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                          style={mergeStyles(
                            heroStyles.inputField,
                            {
                              color: theme ? '#1f2937' : '#f9fafb',
                              backgroundColor: 'transparent'
                            }
                          )}
                        />
                      </View>
                    </YStack>
                  </Animatable.View>

                  {/* Submit Button */}
                  {isSubmitting ? (
                    <Animatable.View animation="fadeIn" duration={300}>
                      <View style={{ alignItems: 'center', padding: 20 }}>
                        <Loading />
                        <SizableText
                          fontSize="$4"
                          color={theme ? '#6b7280' : '#9ca3af'}
                          marginTop={10}
                        >
                          Saving your journal entry...
                        </SizableText>
                      </View>
                    </Animatable.View>
                  ) : (
                    <Animatable.View animation="fadeInUp" delay={500} duration={600}>
                      <LinearGradient
                        colors={['#C8A2C8', '#87CEEB']}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                        style={heroStyles.buttonGradient}
                      >
                        <Button
                          buttonText="Save Journal Entry"
                          onPress={handleSubmit}
                          customStyle={heroStyles.submitButton}
                          textColor="white"
                        />
                      </LinearGradient>
                    </Animatable.View>
                  )}
                </YStack>
              </View>
            </Animatable.View>
          </ScrollView>
        </KeyboardAvoidingView>
      </View>
    </DefaultScreen>
  );
};

// Hero-inspired styling - using Platform.select to avoid web-only CSS properties on native
const heroStyles = StyleSheet.create({
  gradientBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },
  floatingBlur: {
    position: 'absolute',
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: 'rgba(200, 162, 200, 0.2)',
    zIndex: 1,
  },
  glassCard: {
    borderRadius: 24,
    overflow: 'hidden',
    margin: 20,
    marginTop: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: Platform.select({ web: 0.15, default: 0.2 }),
    shadowRadius: 24,
  },
  cardGradientBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },
  blurOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: Platform.select({ web: 'rgba(255, 255, 255, 0.1)', default: 'transparent' }),
    zIndex: 2,
  },
  contentContainer: {
    position: 'relative',
    zIndex: 3,
    backgroundColor: Platform.select({
      web: 'rgba(255, 255, 255, 0.95)',
      default: 'rgba(255, 255, 255, 0.98)',
    }),
  },
  inputContainer: {
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(200, 162, 200, 0.2)',
    backgroundColor: 'transparent',
  },
  inputGradient: {
    padding: 12,
    minHeight: 50,
  },
  inputField: {
    fontSize: 16,
  },
  buttonGradient: {
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#C8A2C8',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    marginTop: 10,
  },
  submitButton: {
    minHeight: 50,
    borderRadius: 16,
    shadowRadius: 0,
    shadowOpacity: 0,
    backgroundColor: 'transparent',
  },
  bottomGradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 80,
    zIndex: 1,
    pointerEvents: 'none',
  },
  entryCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(200, 162, 200, 0.2)',
    overflow: 'hidden',
  },
  filterChip: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(200, 162, 200, 0.3)',
    backgroundColor: 'transparent',
  },
  filterChipActive: {
    backgroundColor: '#C8A2C8',
    borderColor: '#C8A2C8',
  },
  filterChipText: {
    fontSize: 14,
    color: '#6b7280',
  },
  filterChipTextActive: {
    color: '#ffffff',
    fontWeight: '600',
  },
  categoryBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    backgroundColor: 'rgba(200, 162, 200, 0.2)',
  },
  categoryBadgeText: {
    fontSize: 11,
    color: '#C8A2C8',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
});

// Web-only styles that need to be applied conditionally
const webOnlyStyles: {
  floatingBlur?: any;
  glassCard?: any;
  blurOverlay?: any;
  contentContainer?: any;
} = Platform.OS === 'web' ? {
  floatingBlur: {
    filter: 'blur(40px)',
  },
  glassCard: {
    backdropFilter: 'blur(20px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  },
  blurOverlay: {
    backdropFilter: 'blur(20px)',
  },
  contentContainer: {
    backdropFilter: 'blur(20px)',
  },
} : {};

export default JournalEntryScreen;

